import PropTypes from 'prop-types';
import { IconChevronLeftPipe, IconLogout } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { Image } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { findMatchingRoute } from '@/helpers/routes';
import { resetUser } from '@/data/features/user/user';
import { useUserLogoutMutation } from '@/services/api';
import { getMutationResult } from '@/helpers/query';
import classes from './aside.module.css';
import NavbarLink from './NavbarLink';
import Logo from '@/assets/images/logo.svg?react'; // eslint-disable-line import/no-unresolved

const Aside = (props) => {
  const {
    links = [],
    hideCollapse = false,
    defaultOpen = false,
  } = props;

  const { t } = useTranslation();
  const [apiUserLogout] = useUserLogoutMutation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(defaultOpen);
  const collapseLabel = (open) ? t('components.aside.close') : t('components.aside.open');
  const {
    accessToken,
    refreshTokenId,
    refreshToken,
    config,
  } = useSelector((state) => state.user);

  const { logo } = config;

  const handleCollapseClick = () => {
    setOpen(!open);
  };

  const handleLogout = async () => {
    try {
      const logoutData = { refreshTokenId, refreshToken };
      const { isError } = await getMutationResult(logoutData, apiUserLogout);
      if (isError) {
        notifications.show({
          title: t('globals.error.title'),
          message: t('globals.error.message'),
        });
        return;
      }
      dispatch(resetUser());
    } catch (error) {
      notifications.show({
        title: t('globals.error.title'),
        message: t('globals.error.message'),
      });
    }
  };

  const allowallowedRoutes = useSelector((state) => state.user.role.allowallowedRoutes || []);

  const isLinkAllowed = (link) => (
    !link.to || allowallowedRoutes.includes(findMatchingRoute(link.to)?.key)
  );

  const filteredLinks = links.filter(isLinkAllowed);

  const mainLinks = filteredLinks.map((link) => (
    <NavbarLink
      {...link}
      key={link.label}
      disableTooltip={open}
    />
  ));

  const collapse = (!hideCollapse) ? (
    <NavbarLink
      icon={IconChevronLeftPipe}
      label={collapseLabel}
      onClick={handleCollapseClick}
      last={true}
      disableTooltip={open}
    />
  ) : null;

  const logout = (accessToken) ? (
    <NavbarLink
      icon={IconLogout}
      label={t('components.aside.logout')}
      onClick={handleLogout}
      disableTooltip={open}
    />) : null;

  useEffect(() => {
    if (open) {
      document.body.dataset.asideOpen = true;
    } else {
      delete document.body.dataset.asideOpen;
    }

    return () => {
      delete document.body.dataset.asideOpen;
    };
  }, [open]);

  return (
    <div className={classes.aside}>
      <div className={classes.logo}>
        {
          logo ? <Image src={logo} /> : <Logo />
        }
      </div>
      <div className={classes.nav}>
        {mainLinks}
        {logout}
        {collapse}
      </div>
    </div>
  );
};

Aside.propTypes = {
  links: PropTypes.array,
  hideCollapse: PropTypes.bool,
  defaultOpen: PropTypes.bool,
};

export default Aside;
